/**
 * An item's page, before it is yours.
 *
 * Deciding you want a thing and finding it are different acts, and the app used
 * to make you do them in the wrong order: add it, go to the slate, open it, and
 * delete it again if it turned out to be the wrong Dune.
 *
 * The rule this obeys is that a peek may never show something the item page
 * won't — which is why it renders the item page's own components rather than a
 * second, prettier arrangement of the same facts. What is missing is missing
 * because there is no subscription behind it: no refresh (nothing is stored to
 * refresh), no site watch, no notification switches, no history, nothing to
 * mark as read. The web-search link stays, because a link out cannot be wrong
 * and it is the other half of "is this the thing I mean".
 */

import { useEffect, useState } from 'react';
import type { DateSlot, Snapshot } from '../../poller/types.js';
import type { Details } from '../../details/types.js';
import type { SearchResult } from '../../search/types.js';
import { peekCatalogue } from '../client.ts';
import { Options } from '../components/Options.tsx';
import { WikiPanel } from '../components/WikiPanel.tsx';
import { peekItem } from '../lib/derive.js';
import { formatDate } from '../lib/format.js';
import type { WatchItem } from '../lib/model.js';
import { webSearchUrl } from '../lib/websearch.js';
import { ItemDates, ItemHead, PartOf, releaseWord, Seasons, Works } from './DetailBody.tsx';

/**
 * The two fetches fail independently, so "settled" is the only thing worth
 * naming here — which half came back, if either, is read off `snapshot` and
 * `details` directly rather than folded into a second status.
 */
type Looked =
  | { status: 'looking' }
  | { status: 'done'; snapshot: Snapshot | null; details: Details | null };

interface Props {
  result: SearchResult;
  now: Date;
  /**
   * Everything the reader follows, keyed `source:kind:sourceId`. Needed here
   * rather than resolved by the caller because the series a book belongs to is
   * only knowable once the details have arrived — `details.partOf` is fetched,
   * not searched.
   */
  followed: ReadonlyMap<string, WatchItem>;
  busy: boolean;
  /** A failed add, surfaced here rather than in the results list it opened
   *  from — that list is unreachable while the peek is open, so an error left
   *  in `status` would otherwise button-press into silence. */
  status: string | null;
  onBack: () => void;
  onAdd: (formats: DateSlot[], region: string | null) => void;
  onOpenItem: (item: WatchItem) => void;
}

export function Peek({ result, now, followed, busy, status, onBack, onAdd, onOpenItem }: Props) {
  const [looked, setLooked] = useState<Looked>({ status: 'looking' });

  useEffect(() => {
    const controller = new AbortController();
    setLooked({ status: 'looking' });

    peekCatalogue(result, controller.signal)
      .then(({ snapshot, details }) => {
        if (controller.signal.aborted) return;
        setLooked({ status: 'done', snapshot, details });
      })
      .catch(() => {
        if (!controller.signal.aborted) setLooked({ status: 'done', snapshot: null, details: null });
      });

    return () => controller.abort();
    // Identity, not the object: `result` is a fresh object every render of the
    // list it came from, and re-fetching on every render would be a call per
    // keystroke of an unrelated search.
  }, [result.source, result.kind, result.sourceId]);

  const done = looked.status === 'done' ? looked : null;
  const item = peekItem(result, done?.snapshot ?? null, done?.details ?? null);

  // "Book 1 of Rivers of London" is a link when the reader has the series and
  // a plain statement when they don't — `PartOf`'s own rule, given the answer
  // it needs. Null until the fetch lands, which reads the same as not having
  // it: the honest "· not on your slate".
  const partOf = item.details?.partOf;
  const followedSeries = partOf
    ? (followed.get(`${partOf.source}:series:${partOf.sourceId}`) ?? null)
    : null;

  return (
    <>
      <div className="appbar">
        <button className="wordmark" onClick={onBack}>
          ← add
        </button>
      </div>

      <ItemHead item={item} now={now} />

      <div className="actions">
        {/* No refresh: there is nothing stored to refresh. A link out is the
            only other thing this screen can honestly offer. */}
        <a className="action" href={webSearchUrl(item)} target="_blank" rel="noopener noreferrer">
          Search the web ↗
        </a>
      </div>

      {/* Independent of the catalogue fetch — keyless, from the browser — so it
          starts at the same moment and is not held up by it. */}
      <WikiPanel item={item} />

      {looked.status === 'looking' && <p className="hint">Looking it up…</p>}

      {/* ItemDates prints a row per format saying "not announced" — a claim —
          so it is gated on the snapshot half specifically, not on the look-up
          having "found" something. A snapshot fetch can fail while details
          succeeds (they are independent), and rendering ItemDates off details
          alone would assert "not announced" for a book that has a date, or —
          for a series, whose knownPositions lives on the snapshot — fall
          through to format rows a series page never shows at all. */}
      {done && !done.snapshot && (
        <>
          <p className="notice">
            Couldn&apos;t check dates, so this is what the search already knew.
          </p>
          <div className="status-strip quiet">
            <span className="k">{releaseWord(item)}</span>
            <span className="v">
              {result.releaseDate ? formatDate(result.releaseDate) : 'couldn’t check'}
            </span>
          </div>
        </>
      )}

      {done?.snapshot && <ItemDates item={item} now={now} />}

      {/* PartOf, Works and Seasons all read `item.details` — gated on that
          half, same rule, so a details failure alongside a working snapshot
          doesn't leave them rendering off nothing. */}
      {done?.details && (
        <>
          <PartOf item={item} followed={followedSeries} onOpen={onOpenItem} />
          <Works item={item} />
          <Seasons item={item} />
        </>
      )}

      {/* Always offered, whatever the fetch did. Adding must not depend on a
          look-up that only fed a display — the nightly sweep fills the dates
          in either way. */}
      <div className="section" style={{ marginTop: 22 }}>
        <div className="section-head">
          <span className="label">Add to your slate</span>
        </div>
      </div>
      {/* Where a failed add is seen: the results list underneath is not
          reachable while this screen is open, so `status` has nowhere else to
          land. */}
      {status && <p className="notice">{status}</p>}
      <Options result={result} busy={busy} onCancel={onBack} onAdd={onAdd} />
    </>
  );
}
